import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, Check, CheckCheck, Trash2, Mail, FileText, Receipt, ShoppingCart, Calendar, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications');
      // API returns {notifications: [], unread_count: 0}
      const data = response.data;
      setNotifications(data.notifications || data || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'quote_sent':
      case 'quote_accepted':
      case 'quote_rejected':
        return <FileText className="h-5 w-5" />;
      case 'invoice_sent':
      case 'invoice_paid':
        return <Receipt className="h-5 w-5" />;
      case 'new_po':
      case 'po_completed':
        return <ShoppingCart className="h-5 w-5" />;
      case 'job_update':
        return <Info className="h-5 w-5" />;
      case 'schedule_reminder':
        return <Calendar className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getNotificationColor = (type) => {
    if (type?.includes('accepted') || type?.includes('paid') || type?.includes('completed')) {
      return 'text-green-500 bg-green-500/10';
    }
    if (type?.includes('rejected')) {
      return 'text-red-500 bg-red-500/10';
    }
    if (type?.includes('reminder') || type?.includes('warning')) {
      return 'text-yellow-500 bg-yellow-500/10';
    }
    return 'text-primary bg-primary/10';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bell className="h-8 w-8 text-primary" />
            Notifications
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">{unreadCount}</Badge>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">Stay updated on your business activities</p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead} size="sm">
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'all', label: 'All', count: notifications.length },
          { id: 'unread', label: 'Unread', count: unreadCount },
          { id: 'read', label: 'Read', count: notifications.length - unreadCount },
        ].map(tab => (
          <Button
            key={tab.id}
            variant={filter === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilter(tab.id)}
          >
            {tab.label} ({tab.count})
          </Button>
        ))}
      </div>

      {/* Notifications List */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Bell className="h-16 w-16 mb-4 opacity-30" />
              <p className="text-lg">No notifications</p>
              <p className="text-sm">{filter === 'unread' ? 'All caught up!' : 'Nothing to show here'}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-secondary/30 transition-colors ${
                    !notification.is_read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-full ${getNotificationColor(notification.notification_type)}`}>
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className={`font-medium ${!notification.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {notification.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-muted-foreground">{formatTime(notification.created_at)}</span>
                          {!notification.is_read && (
                            <div className="w-2 h-2 rounded-full bg-primary" />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {notification.link && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.href = notification.link}
                          >
                            View Details
                          </Button>
                        )}
                        {!notification.is_read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Mark Read
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Push Notification Setup Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Enable browser push notifications to get instant alerts even when you're not on this page.
          </p>
          <Button
            onClick={async () => {
              if ('Notification' in window) {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                  toast.success('Push notifications enabled!');
                  // Subscribe to push notifications
                  try {
                    await api.post('/notifications/push-subscribe', {
                      subscription: { enabled: true }
                    });
                  } catch (e) {
                    console.error('Failed to subscribe:', e);
                  }
                } else {
                  toast.error('Push notifications denied');
                }
              } else {
                toast.error('Push notifications not supported in this browser');
              }
            }}
          >
            <Bell className="h-4 w-4 mr-2" />
            Enable Push Notifications
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
